import React, { useState, useEffect } from "react";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Import des images
import logoBlue from "../../assets/logoBlue.svg";
import logoJaune from "../../assets/logoJaune.svg";
import telephone from "../../assets/telephone.svg";
import mailIcon from "../../assets/mailIcon.svg";
import mail from "../../assets/mail.png";
import PhotoFilleHP from "../../assets/photo_principale.png";
import PhotoGarçonHP from "../../assets/PhotoGarçonHP.png";
import pdgPhoto from "../../assets/RMX02068.avif";

// SVG pour les services
const CoursParticuliersSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const PreparationExamenSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const SoutienScolaireSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
    <polyline points="2 17 12 22 22 17"></polyline>
    <polyline points="2 12 12 17 22 12"></polyline>
  </svg>
);

const MethodologieSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
  </svg>
);

// SVG pour les atouts - Icônes simplifiées
const EnseignantsQualifiesSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const AccompagnementPersonnaliseSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
  </svg>
);

const ReactiviteSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const PartenariatEtablissementsSVG = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#131cc9"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
    <polyline points="9,22 9,12 15,12 15,22"></polyline>
  </svg>
);

// Header fixe toujours visible
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAuthenticated = useIsAuthenticated?.() ?? false;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  return (
    <>
      <motion.header
        className={`py-3 flex items-center justify-between px-4 sm:px-8 fixed w-full z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md" : "bg-white shadow-sm"
        }`}
        initial={{ y: 0 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <a href="/" className="flex items-center">
          <img
            src={logoBlue}
            alt="Logo Objectif Révision"
            className="w-8 h-8 sm:w-10 sm:h-10"
          />
          <div className="ml-2 font-semibold text-blue-800 text-sm sm:text-base">
            <span className="block">Objectif</span>
            <span className="block">Révision</span>
          </div>
        </a>
        <nav className="hidden md:flex space-x-4 lg:space-x-8">
          <NavLink text="Accueil" active={true} scrollToId="root" />
          <NavLink text="Nos Atouts" scrollToId="services" />
          <motion.a
            href="https://blog.objectif-revision.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-montserrat font-bold text-gray-700 hover:text-blue-800 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Blog
          </motion.a>
          <NavLink text="Contacts" scrollToId="contacts" />
          {isAuthenticated() ? (
            <NavLink href="/dashboard" text="Tableau de bord" />
          ) : (
            <motion.a
              href="/login"
              className="font-montserrat font-bold text-white bg-blue-800 px-3 py-2 rounded-lg hover:bg-blue-900 transition-colors text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Accès utilisateur
            </motion.a>
          )}
        </nav>
        <motion.button
          className="md:hidden text-blue-800"
          whileTap={{ scale: 0.9 }}
          onClick={toggleMobileMenu}
          aria-label="Ouvrir le menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </motion.button>
      </motion.header>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex">
          <div className="bg-white w-64 h-full shadow-lg p-6 flex flex-col">
            <button
              className="self-end mb-8"
              onClick={toggleMobileMenu}
              aria-label="Fermer le menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <button
              type="button"
              className="mb-4 font-bold text-blue-800 text-left"
              onClick={() => {
                document
                  .getElementById("root")
                  ?.scrollIntoView({ behavior: "smooth" });
                toggleMobileMenu();
              }}
            >
              Accueil
            </button>
            <button
              type="button"
              className="mb-4 font-bold text-blue-800 text-left"
              onClick={() => {
                document
                  .getElementById("services")
                  ?.scrollIntoView({ behavior: "smooth" });
                toggleMobileMenu();
              }}
            >
              Nos Atouts
            </button>
            <a
              href="https://blog.objectif-revision.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-4 font-bold text-blue-800 block"
              onClick={toggleMobileMenu}
            >
              Blog
            </a>
            <button
              type="button"
              className="mb-4 font-bold text-blue-800 text-left"
              onClick={() => {
                document
                  .getElementById("contacts")
                  ?.scrollIntoView({ behavior: "smooth" });
                toggleMobileMenu();
              }}
            >
              Contacts
            </button>
            {isAuthenticated() ? (
              <a
                href="/dashboard"
                className="mb-4 font-bold text-blue-800"
                onClick={toggleMobileMenu}
              >
                Tableau de bord
              </a>
            ) : (
              <a
                href="/login"
                className="font-bold text-white bg-blue-800 px-4 py-2 rounded-lg mt-4"
                onClick={toggleMobileMenu}
              >
                Accès utilisateur
              </a>
            )}
          </div>
          <div className="flex-1" onClick={toggleMobileMenu} />
        </div>
      )}
    </>
  );
};

// Typage de NavLink
interface NavLinkProps {
  href?: string;
  text: string;
  active?: boolean;
  scrollToId?: string;
}
const NavLink: React.FC<NavLinkProps> = ({
  href,
  text,
  active = false,
  scrollToId,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (scrollToId) {
      e.preventDefault();
      document
        .getElementById(scrollToId)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <motion.a
      href={href || (scrollToId ? `#${scrollToId}` : undefined)}
      className={`font-montserrat font-bold text-base relative ${
        active ? "text-blue-800" : "text-gray-700"
      }`}
      whileHover={{ scale: 1.05 }}
      onClick={handleClick}
    >
      {text}
      {active && (
        <motion.span
          className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-800"
          layoutId="underline"
        />
      )}
    </motion.a>
  );
};

// Hero Section (Banner 1 enhanced) avec ajustement pour la navbar fixe
const HeroSection = () => (
  <div className="bg-gradient-to-r from-blue-800 to-blue-900 min-h-screen flex items-center pt-24 overflow-hidden">
    <div className="container mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center">
      <motion.div
        className="lg:w-1/2 text-white text-left"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
          Réussissez vos études avec{" "}
          <span className="text-e3b505">Objectif Révision</span>
        </h1>
        <p className="text-lg sm:text-xl mb-8">
          La plateforme qui accompagne les élèves des collèges et lycées à
          l'excellence en mathématiques.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-start relative z-10">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <button
              type="button"
              className="bg-white text-blue-800 font-bold py-3 px-6 rounded-lg relative z-10 w-full sm:w-auto text-center min-w-[200px]"
              onClick={() => {
                document
                  .getElementById("services")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Découvrir nos atouts
            </button>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <a
              href="mailto:contact@objectif-revision.com?subject=Demande d'information - Objectif Révision&body=Bonjour,%0D%0A%0D%0AJe souhaite obtenir plus d'informations sur vos services.%0D%0A%0D%0AMerci de me recontacter.%0D%0A%0D%0ACordialement,"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-e3b505 text-white font-bold py-3 px-8 rounded-lg text-lg w-full sm:w-auto text-center min-w-[200px]"
            >
              Nous contacter
            </a>
          </motion.div>
        </div>
      </motion.div>
      <motion.div
        className="lg:w-1/2 mt-10 lg:mt-0 relative hidden lg:block"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="relative mx-auto max-w-md">
          <div className="absolute -left-6 -top-6 w-72 h-72 bg-e3b505 rounded-lg transform -rotate-6 z-0"></div>
          <img
            src={PhotoFilleHP}
            alt="Étudiant utilisant Objectif Révision"
            className="relative z-10 rounded-lg  w-full"
          />
          <div className="absolute -right-4 -bottom-4 w-40 h-40 bg-blue-600 rounded-lg transform rotate-12 z-0"></div>
        </div>
      </motion.div>
    </div>
  </div>
);

// Typage de ServiceCard
interface ServiceCardProps {
  Icon: React.ElementType;
  title: string;
  description: string;
}
const ServiceCard: React.FC<ServiceCardProps> = ({
  Icon,
  title,
  description,
}) => (
  <motion.div
    className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center"
    whileHover={{
      y: -10,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <div className="bg-blue-50 p-4 rounded-full mb-4">
      <Icon />
    </div>
    <h3 className="text-xl font-bold text-blue-800 mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

// Services Section (Banner 2 enhanced) - Maintenant Atouts
const ServicesSection = () => (
  <div id="services" className="py-20 bg-gray-50 overflow-hidden">
    <div className="container mx-auto px-4 sm:px-6">
      <motion.div
        className="text-left mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold mb-2">
          <span className="text-black">NOS </span>
          <span className="text-blue-800">ATOUTS</span>
        </h2>
        <h3 className="text-xl sm:text-2xl font-bold mb-4">
          CE QUI REND OBJECTIF RÉVISION UNIQUE
        </h3>
        <div className="h-1 w-24 bg-e3b505 mb-6"></div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Enseignants qualifiés */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          whileHover={{
            y: -10,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start space-x-4">
            <div className="bg-blue-50 p-3 rounded-full flex-shrink-0">
              <EnseignantsQualifiesSVG />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-3">
                Enseignants qualifiés
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Des professeurs rigoureusement sélectionnés pour leur expertise
                en mathématiques et leur capacité à transmettre efficacement.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Accompagnement personnalisé */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          whileHover={{
            y: -10,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start space-x-4">
            <div className="bg-blue-50 p-3 rounded-full flex-shrink-0">
              <AccompagnementPersonnaliseSVG />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-3">
                Accompagnement personnalisé
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Un suivi adapté au niveau de chaque élève, qui répond à ses
                difficultés précises et l'aide à progresser à son rythme.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Réactivité */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          whileHover={{
            y: -10,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start space-x-4">
            <div className="bg-blue-50 p-3 rounded-full flex-shrink-0">
              <ReactiviteSVG />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-3">
                Réactivité
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Un enseignant disponible quand l'élève en a besoin, pour
                débloquer rapidement une situation et éviter toute perte de
                temps.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Partenariat avec les établissements */}
        <motion.div
          className="bg-white rounded-xl shadow-lg p-8"
          whileHover={{
            y: -10,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="flex items-start space-x-4">
            <div className="bg-blue-50 p-3 rounded-full flex-shrink-0">
              <PartenariatEtablissementsSVG />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-800 mb-3">
                Partenariat avec les établissements
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Une collaboration directe avec les collèges et lycées,
                garantissant une meilleure intégration du soutien dans le
                parcours scolaire.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
);

// About Section (Banner 3 enhanced)
const AboutSection = () => (
  <div className="py-20 bg-white overflow-hidden">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="flex flex-col lg:flex-row items-center">
        <motion.div
          className="lg:w-1/2 relative mb-12 lg:mb-0 hidden lg:block"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative mx-auto max-w-lg">
            <div className="w-60 h-80 origin-top-left rotate-[-2.70deg] bg-e3b505 absolute top-0 left-0"></div>
            <img
              className="w-80 h-auto origin-top-left rotate-[-4.82deg] relative z-10"
              src={PhotoFilleHP}
              alt="Étudiante"
            />
            <div className="w-60 h-80 origin-bottom-left rotate-[2.65deg] bg-blue-800 absolute bottom-0 left-0"></div>
            <img
              className="w-80 h-auto origin-bottom-left rotate-[3.95deg] relative z-20 absolute bottom-0"
              src={PhotoGarçonHP}
              alt="Étudiant"
            />
          </div>
        </motion.div>

        <motion.div
          className="lg:w-1/2 lg:pl-12 text-left"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex mb-4 justify-start">
            <h2 className="text-2xl sm:text-3xl font-bold">
              <span className="text-black uppercase">À propos d'</span>
              <span className="text-e3b505 uppercase font-bold">Objectif </span>
              <span className="text-blue-800 uppercase font-bold">
                Révision
              </span>
            </h2>
          </div>

          <h3 className="text-xl sm:text-2xl font-bold mb-6 uppercase">
            Une plateforme pensée pour répondre aux vrais besoins des élèves :
            comprendre, pratiquer, progresser.
          </h3>

          <p className="text-gray-600 text-base sm:text-lg mb-8">
            Sur Objectif Révision, chaque élève bénéficie d'un accompagnement
            adapté à sa situation. Notre plateforme propose différents types de
            soumissions qui permettent de répondre à tous les problèmes
            rencontrés lors d’un exercice ou d’une épreuve, garantissant que
            chaque élève trouve le type d'accompagnement qui correspond
            exactement à son besoin du moment.
          </p>

          <p className="text-gray-600 text-base sm:text-lg mb-8">
            Notre équipe d'enseignants spécialisés ne se limite pas à fournir
            une réponse : elle guide l'élève dans son apprentissage, l'encourage
            à réfléchir par lui-même et l'aide à acquérir des méthodes de
            travail efficaces. Nous proposons également un espace dédié aux
            parents d'élèves, afin de suivre la progression et les actions de
            chacun de leurs enfants inscrits sur notre plateforme.
          </p>

          {/* Bouton 'En savoir plus' supprimé */}
        </motion.div>
      </div>
    </div>
  </div>
);

// Mot du PDG Section
const PDGMessageSection = () => (
  <div className="py-20 bg-gray-50 overflow-hidden">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="flex flex-col lg:flex-row items-start">
        {/* Image - visible sur mobile en premier */}
        <motion.div
          className="lg:w-1/2 lg:pr-12 flex justify-center mb-8 lg:mb-0 order-1 lg:order-1"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative">
            <img
              src={pdgPhoto}
              alt="Mardoché Clabessi - Directeur administratif"
              className="relative z-10 w-80 h-96 sm:w-65 sm:h-80 object-cover rounded-lg shadow-xl"
              style={{ objectPosition: "center top" }}
            />
          </div>
        </motion.div>

        {/* Contenu texte - visible sur mobile en second */}
        <motion.div
          className="lg:w-1/2 text-left order-2 lg:order-2"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Citation en premier sur mobile */}
          <div className="text-gray-700 text-lg leading-relaxed mb-6">
            <p className="mb-4 italic">
              "Objectif Révision est une plateforme innovante qui accompagne les
              collégiens et lycéens vers la réussite en mathématiques. Grâce à
              des partenariats directs avec les établissements scolaires, nous
              offrons aux élèves un soutien personnalisé assuré par des
              enseignants qualifiés, sélectionnés par notre équipe."
            </p>
            <p className="italic">
              "Notre mission est simple : permettre à chaque élève de progresser
              et de viser l'excellence."
            </p>
          </div>

          {/* Informations du PDG après la citation sur mobile */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Mardoché CLABESSI
            </h3>
            <p className="text-gray-600 mb-2">
              Directeur administratif de Objectif Révision
            </p>
            <p className="text-gray-600 mb-2">
              Champion du Bénin aux Olympiades Panafricaines de Mathématiques
              2018
            </p>
            <p className="text-gray-600">
              Ingénieur centralien, mathématiques appliquées
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
);

// Testimonials Section (for future use)
const TestimonialsSection = () => (
  <div className="py-20 bg-gray-50">
    <div className="container mx-auto px-6">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold mb-2">
          <span className="text-black">CE QUE </span>
          <span className="text-blue-800">DISENT</span>
        </h2>
        <h3 className="text-2xl font-bold mb-4">NOS ÉTUDIANTS</h3>
        <div className="h-1 w-24 bg-e3b505 mx-auto mb-6"></div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Témoignage 1 */}
        <motion.div
          className="bg-white p-6 rounded-xl shadow-lg"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 font-bold text-xl">
              S
            </div>
            <div className="ml-4">
              <h4 className="font-bold">Sophie M.</h4>
              <p className="text-gray-600">Bac S - Mention Très Bien</p>
            </div>
          </div>
          <p className="text-gray-700">
            "Grâce aux cours particuliers d'Objectif Révision, j'ai pu combler
            mes lacunes en mathématiques et obtenir une mention très bien au
            bac. Les professeurs sont compétents et bienveillants."
          </p>
          <div className="flex text-e3b505 mt-4">★★★★★</div>
        </motion.div>

        {/* Témoignage 2 */}
        <motion.div
          className="bg-white p-6 rounded-xl shadow-lg"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 font-bold text-xl">
              T
            </div>
            <div className="ml-4">
              <h4 className="font-bold">Thomas L.</h4>
              <p className="text-gray-600">Étudiant en CPGE</p>
            </div>
          </div>
          <p className="text-gray-700">
            "La méthodologie que j'ai apprise m'a permis de mieux organiser mon
            travail en prépa. Je gagne un temps précieux et mes résultats
            s'améliorent constamment."
          </p>
          <div className="flex text-e3b505 mt-4">★★★★★</div>
        </motion.div>

        {/* Témoignage 3 */}
        <motion.div
          className="bg-white p-6 rounded-xl shadow-lg"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-800 font-bold text-xl">
              A
            </div>
            <div className="ml-4">
              <h4 className="font-bold">Aminata D.</h4>
              <p className="text-gray-600">Étudiante en L2 Droit</p>
            </div>
          </div>
          <p className="text-gray-700">
            "Le soutien scolaire en ligne m'a permis de continuer à progresser
            malgré mon emploi du temps chargé. Les cours sont flexibles et
            parfaitement adaptés à mes besoins."
          </p>
          <div className="flex text-e3b505 mt-4">★★★★☆</div>
        </motion.div>
      </div>
    </div>
  </div>
);

// Call-to-Action Section
const CTASection = () => (
  <motion.div
    className="py-20 bg-gradient-to-br from-blue-800 via-blue-900 to-blue-800 text-white relative overflow-hidden"
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    transition={{ duration: 0.8 }}
    viewport={{ once: true }}
  >
    {/* Background pattern */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute top-0 left-0 w-72 h-72 bg-e3b505 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
      <div className="absolute top-0 right-0 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
    </div>

    <div className="container mx-auto px-6 relative z-10">
      <div className="text-center mb-12">
        <motion.h2
          className="text-4xl md:text-5xl font-bold mb-4"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Envie d'en savoir plus ?
        </motion.h2>
        <div className="w-24 h-1 bg-e3b505 mx-auto mb-8"></div>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <motion.div
            className="text-center p-6 rounded-xl bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <div className="w-16 h-16 bg-e3b505 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3">Responsables d'écoles</h3>
            <p className="text-blue-100 leading-relaxed text-justify">
              Proposez à vos élèves un outil innovant qui complète efficacement
              les cours et améliore les résultats de votre établissement.
            </p>
          </motion.div>

          <motion.div
            className="text-center p-6 rounded-xl bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <div className="w-16 h-16 bg-e3b505 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3">Parents</h3>
            <p className="text-blue-100 leading-relaxed text-justify">
              Offrez à votre enfant un accompagnement personnalisé et un suivi
              par des enseignants qualifiés.
            </p>
          </motion.div>

          <motion.div
            className="text-center p-6 rounded-xl bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <div className="w-16 h-16 bg-e3b505 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-3">Élèves</h3>
            <p className="text-blue-100 leading-relaxed text-justify">
              Débloquez vos difficultés, gagnez en confiance et atteignez
              l'excellence en mathématiques.
            </p>
          </motion.div>
        </div>

        <motion.div
          className="text-center mb-12"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed text-justify">
            Que vous soyez responsable d'un collège ou d'un lycée, parent
            d'élève ou élève motivé, contactez-nous dès aujourd'hui pour obtenir
            toutes les informations nécessaires, ou prenez rendez-vous avec
            notre équipe pour découvrir concrètement comment la plateforme peut
            répondre à vos besoins.
          </p>
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row gap-6 justify-center"
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <a
              href="mailto:contact@objectif-revision.com?subject=Demande d'information - Objectif Révision&body=Bonjour,%0D%0A%0D%0AJe souhaite obtenir plus d'informations sur vos services.%0D%0A%0D%0AMerci de me recontacter.%0D%0A%0D%0ACordialement,"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-e3b505 text-white font-bold py-4 px-10 rounded-xl text-lg w-full sm:w-auto text-center min-w-[220px] shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Nous contacter
            </a>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto"
          >
            <a
              href="https://calendly.com/clabessim/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-blue-800 font-bold py-4 px-10 rounded-xl text-lg w-full sm:w-auto text-center min-w-[220px] shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Prendre rendez-vous
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

// Footer enhanced
const Footer = () => (
  <footer className="bg-090b3a text-white pt-12 pb-6">
    <div id="contacts"></div>
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 justify-center">
        {/* Logo et description */}
        <div>
          <div className="flex items-center mb-4">
            <img
              src={logoJaune}
              alt="Logo Objectif Révision"
              className="h-10"
            />
            <div className="ml-2 font-semibold">
              <span className="block">Objectif</span>
              <span className="block">Révision</span>
            </div>
          </div>
          <p className="text-gray-300 mb-6">
            Accompagnement scolaire personnalisé pour tous les niveaux. Des
            solutions adaptées pour progresser et réussir.
          </p>
          <motion.button
            type="button"
            className="inline-block bg-e3b505 text-white font-medium py-2 px-4 rounded-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              // Scroll vers la section About (qui n'a pas d'ID, on peut ajouter un ID ou scroll vers services)
              document
                .getElementById("services")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            En savoir plus
          </motion.button>
        </div>

        {/* Liens rapides */}
        <div>
          <h3 className="text-lg font-bold mb-4 uppercase">Liens rapides</h3>
          <ul className="space-y-2">
            <li>
              <button
                type="button"
                className="hover:text-e3b505 transition-colors text-left"
                onClick={() =>
                  document
                    .getElementById("root")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Accueil
              </button>
            </li>
            <li>
              <button
                type="button"
                className="hover:text-e3b505 transition-colors text-left"
                onClick={() =>
                  document
                    .getElementById("services")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Nos Atouts
              </button>
            </li>
            <li>
              <button
                type="button"
                className="hover:text-e3b505 transition-colors text-left"
                onClick={() =>
                  document
                    .getElementById("contacts")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Contactez-nous
              </button>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-lg font-bold mb-4 uppercase">Contactez-nous</h3>
          <div className="flex items-center mb-4">
            <img src={telephone} alt="Téléphone" className="w-5 h-5 mr-3" />
            <span>(+229) 01 67 26 00 40</span>
          </div>
          <div className="flex items-center">
            <img src={mailIcon} alt="Mail" className="w-5 h-5 mr-3" />
            <span>contact@objectif-revision.com</span>
          </div>
        </div>

        {/* Newsletter supprimée */}
      </div>

      <div className="border-t border-e3b505 mt-8 pt-6 text-center text-gray-400 text-sm">
        <p>© 2025 Visionotup. Tous droits réservés.</p>
      </div>
    </div>
  </footer>
);

// Notification de cookies
const CookieNotification = () => {
  const [showCookieNotification, setShowCookieNotification] = useState(true);

  const acceptCookies = () => {
    setShowCookieNotification(false);
    localStorage.setItem("cookiesAccepted", "true");
  };

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");
    if (cookiesAccepted === "true") {
      setShowCookieNotification(false);
    }
  }, []);

  if (!showCookieNotification) return null;

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 bg-blue-800 text-white p-4 z-50 shadow-lg"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1 text-center sm:text-left">
          <p className="text-sm sm:text-base">
            <strong>Politique de cookies :</strong> Ce site utilise des cookies
            essentiels pour l'authentification et la sécurité de votre session.
            Ces cookies sont nécessaires au bon fonctionnement de l'application
            et ne peuvent pas être désactivés.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={acceptCookies}
            className="bg-e3b505 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600 transition-colors"
          >
            J'accepte
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Page d'accueil complète
const Home = () => (
  <div id="root" className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-grow">
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <PDGMessageSection />
      {/* <TestimonialsSection /> */}
      <CTASection />
    </main>
    <Footer />
    <CookieNotification />
  </div>
);

export default Home;
